import { useCallback, useEffect } from "react";

import { css } from "@compiled/react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives";
import { N900 } from "@atlaskit/theme/colors";
import FeatureGates from "@atlassian/feature-gate-js-client";

import ErrorBoundary from "@/components/error-boundary";
import { GlobalFooter } from "@/patterns/global-footer/src";

import { useAnalyticsClient } from "../../../analytics/wac-analytics/analytics-client";

import JpdUseCaseProductTourOnWac from "./experiment-jpd-use-case/sections/hero";
import CTASection, { CTASectionProps } from "./sections/cta";
import CustomersSection from "./sections/customers";
import HeroSection from "./sections/hero";
import JiraProductDiscoveryNavV2 from "./sections/product-nav";
import RegistrationBanner from "./sections/register-banner";
import SideBySideSection, {
    SideBySideSectionProps
} from "./sections/side-by-side";
import TabsSection, { TabsSectionProps } from "./sections/tabs";
import VideoSection from "./sections/video";
import WorkflowSection, { WorkflowSectionProps } from "./sections/workflow";

const pageStyle = css({
    color: N900
});

const heroContainerStyle = css({
    paddingBlockStart: "4rem",
    display: "flex",
    flexDirection: "column",
    gap: "2rem",
    alignItems: "stretch",

    backgroundImage: `
        url("https://wac-cdn.atlassian.com/misc-assets/webp-images/jpd/hero-bg.webp"),
        url("https://wac-cdn.atlassian.com/misc-assets/webp-images/jpd/hero-bg2.webp")`,
    backgroundRepeat: "no-repeat",
    backgroundSize: "400px, 280px",
    backgroundPosition: "top left, top 230px right -40px",

    [media.above.sm]: {
        paddingBlockStart: "6rem"
    },
    [media.only.sm]: {
        backgroundSize: "440px, 280px"
    },
    [media.above.md]: {
        backgroundImage: `
            url("https://wac-cdn.atlassian.com/misc-assets/webp-images/jpd/hero-bg.webp"),
            url("https://wac-cdn.atlassian.com/misc-assets/webp-images/jpd/hero-bg2.webp"),
            url("https://wac-cdn.atlassian.com/misc-assets/webp-images/jpd/hero-bg3.webp"),
            url("https://wac-cdn.atlassian.com/misc-assets/webp-images/jpd/tab-bg2.webp")`,
        backgroundPosition:
            "top left, top 160px right, left bottom 160px, top 260px left 100px",
        backgroundSize: "570px, 400px, 225px, 180px"
    }
});

export interface JiraProductDiscoveryTemplateProps {
    sideBySideSectionProps: SideBySideSectionProps;
    tabSectionProps: TabsSectionProps;
    workflowSectionProps: WorkflowSectionProps;
    cTASectionProps: CTASectionProps;
}

type ExperimentCohorts = "control" | "test" | "not-enrolled";

export const JiraProductDiscoveryTemplate = ({
    sideBySideSectionProps,
    tabSectionProps,
    workflowSectionProps,
    cTASectionProps
}: JiraProductDiscoveryTemplateProps) => {
    const isJpdUseCaseProductTourOnWacEnabled =
        FeatureGates.getExperimentValue<ExperimentCohorts>(
            "jpd_use_case_product_tour_on_wac",
            "heroWithTabs",
            "not-enrolled"
        );

    const analyticsClient = useAnalyticsClient();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const fireExposureProxyEvent = useCallback(() => {
        analyticsClient &&
            createAnalyticsEvent({
                name: "wacJpdProductTourPage",
                eventType: "screen",
                action: "viewed",
                actionSubject: "page",
                attributes: {
                    experiment: "jpd_product_roadmap_template",
                    isJpdUseCaseProductTourOnWacEnabled
                }
            }).fire();
    }, [
        analyticsClient,
        createAnalyticsEvent,
        isJpdUseCaseProductTourOnWacEnabled
    ]);

    const fireOperationalErrorEvent = useCallback(
        (error: Error) => {
            analyticsClient,
                createAnalyticsEvent({
                    event: "failed",
                    action: "error",
                    eventType: "operational",
                    actionSubject: "feature",
                    attributes: {
                        error: error.message,
                        experiment: "jpd_use_case_product_tour_on_wac"
                    },
                    error
                }).fire();
        },
        [createAnalyticsEvent, analyticsClient]
    );

    useEffect(() => {
        isJpdUseCaseProductTourOnWacEnabled !== "not-enrolled" &&
            fireExposureProxyEvent();
    }, [fireExposureProxyEvent, isJpdUseCaseProductTourOnWacEnabled]);

    return (
        <div css={pageStyle}>
            <JiraProductDiscoveryNavV2 />
            <div
                css={
                    isJpdUseCaseProductTourOnWacEnabled !== "test" &&
                    heroContainerStyle
                }
            >
                {isJpdUseCaseProductTourOnWacEnabled === "test" ? (
                    <ErrorBoundary
                        fallback={
                            <>
                                <HeroSection />
                                <VideoSection />
                                <CustomersSection />
                            </>
                        }
                        onError={(error) => fireOperationalErrorEvent(error)}
                    >
                        <JpdUseCaseProductTourOnWac />
                    </ErrorBoundary>
                ) : (
                    <>
                        <HeroSection />
                        <VideoSection />
                        <CustomersSection />
                    </>
                )}

                <RegistrationBanner />
            </div>
            <SideBySideSection {...sideBySideSectionProps} />
            <TabsSection {...tabSectionProps} />
            <WorkflowSection {...workflowSectionProps} />
            <CTASection {...cTASectionProps} />
            <GlobalFooter />
        </div>
    );
};
